/**
 * Billing: what this organisation is on, what it has left, and how to change either (Phase 7).
 *
 * The money happens in Stripe - Checkout to buy, the Customer Portal to change a card, switch plan
 * or cancel. This page only says plainly what is true: the plan, the credit, when that credit
 * expires (it does not roll over, decision 7.3), and what each plan would cost.
 */

import { CreditCard, Download, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { downloadFile } from "@/api/client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useBilling, useBillingHistory, useCheckout, usePortal } from "@/lib/queries";
import { useBranding } from "@/lib/useBranding";

export function BillingPage() {
  const billing = useBilling();
  const { brand } = useBranding();
  const checkout = useCheckout();
  const portal = usePortal();
  const view = billing.data;
  const paid = new URLSearchParams(window.location.search).has("paid");

  if (!view) {
    return (
      <section className="mx-auto max-w-4xl px-6 py-10 text-sm text-muted-foreground">
        {billing.isLoading ? "Loading…" : "Billing is unavailable just now."}
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-4xl space-y-6 bg-[rgb(15_17_21/0.88)] px-6 py-10 backdrop-blur-sm">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Billing</h1>
        <p className="text-sm text-muted-foreground">
          Your plan, your credit, and what each plan includes. {view.vat_note}
        </p>
      </header>

      {view.mode === "demo" && (
        <Alert>
          <AlertDescription>
            <strong>Demo billing.</strong> This copy runs against a local stand-in, so nothing here reaches Stripe:
            no money moves, no card is taken, and any credit granted was paid for by nobody.
          </AlertDescription>
        </Alert>
      )}

      {paid && (
        <Alert>
          <AlertDescription>
            Thank you - payment received. Your plan and credit appear here as soon as Stripe confirms it, usually
            within a few seconds.
          </AlertDescription>
        </Alert>
      )}

      {view.status === "past_due" && (
        <Alert variant="destructive">
          <AlertDescription>
            The last payment failed, so live runs are paused. Your studies and results are untouched. Update the card
            in the billing portal and they resume.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <Figure label="Plan" value={view.plans.find((p) => p.code === view.plan)?.name ?? view.plan}
                note={view.cancel_at_period_end ? "ends at the period end" : view.status} />
        <Figure label="Credit left" value={`$${view.credit_available_usd.toFixed(2)}`}
                note={view.credit_held_usd > 0 ? `$${view.credit_held_usd.toFixed(2)} reserved by work running now` : undefined} />
        <Figure
          label="Credit expires"
          value={view.credits_expire_at ? new Date(view.credits_expire_at).toLocaleDateString() : "–"}
          note="a plan's monthly credit does not roll over"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          disabled={!view.can_pay || checkout.isPending}
          onClick={() => checkout.mutate({ topup: true })}
        >
          <CreditCard aria-hidden="true" /> Buy ${view.topup_usd.toFixed(0)} of credit
        </Button>
        {view.status !== "none" && (
          <Button variant="ghost" disabled={portal.isPending} onClick={() => portal.mutate()}>
            <ExternalLink aria-hidden="true" /> Card, plan and cancellation
          </Button>
        )}
      </div>

      {!view.can_pay && (
        <Alert>
          <AlertDescription>
            Paying is not switched on in this deployment yet, so plans cannot be bought here.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {view.plans.map((plan) => (
          <article
            key={plan.code}
            className={cn(
              "rounded-xl border p-4",
              plan.current ? "border-step-done bg-step-done/10" : "border-white/10 bg-[#16181d]",
            )}
          >
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="font-medium">{plan.name}</h2>
              <p className="text-lg font-semibold tabular-nums">
                {plan.monthly_price_usd === 0 ? "Free" : `$${plan.monthly_price_usd.toFixed(0)}`}
                {plan.monthly_price_usd > 0 && <span className="text-xs text-muted-foreground"> / month</span>}
              </p>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {plan.included_credit_usd > 0
                ? `Includes $${plan.included_credit_usd.toFixed(0)} of usage credit each month.`
                : "A one-off trial credit to try it with."}
            </p>
            <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              {typeof plan.limits.studies === "number" && <li>{plan.limits.studies} studies</li>}
              {typeof plan.limits.seats === "number" && <li>{plan.limits.seats} seats</li>}
              {plan.limits.goal_seek === true && <li>Goal seek</li>}
              {plan.limits.holdout === true && <li>Holdout testing</li>}
            </ul>
            <div className="mt-3">
              {plan.current ? (
                <p className="text-xs text-step-done">Your plan</p>
              ) : plan.purchasable ? (
                <Button size="sm" disabled={!view.can_pay || checkout.isPending}
                        onClick={() => checkout.mutate({ plan: plan.code })}>
                  {checkout.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
                  Choose {plan.name}
                </Button>
              ) : plan.self_serve ? (
                <p className="text-xs text-muted-foreground">Not available yet</p>
              ) : (
                <a className="text-xs text-sky-300 underline underline-offset-2"
                   href={`mailto:${brand.support_email}`}>
                  Talk to us
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      <History />

      <YourData />

      <p className="text-xs text-muted-foreground">
        Payments are handled by Stripe; no card details reach this service. See the{" "}
        <Link className="underline underline-offset-2" to="/terms">Terms of Service</Link> and{" "}
        <Link className="underline underline-offset-2" to="/privacy">Privacy Policy</Link>.
      </p>
    </section>
  );
}

/**
 * Taking everything away with you, which the privacy policy promises and nothing used to do.
 *
 * Results have always downloaded a run at a time; this is the whole account in one file, including
 * the studies, audiences and billing history that had no download at all.
 */
function YourData() {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <section className="space-y-2 rounded-xl border border-white/10 bg-[#16181d] p-4">
      <h2 className="text-sm font-medium">Your data</h2>
      <p className="text-xs text-muted-foreground">
        Everything this account holds, in one file: your studies and their versions, every
        run&rsquo;s results, your audiences, and this billing history. Large result files are listed
        rather than included &mdash; download those from the run they belong to.
      </p>
      {failed && (
        <Alert variant="destructive">
          <AlertDescription>That did not download. Try again in a moment.</AlertDescription>
        </Alert>
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setFailed(false);
          downloadFile("/api/v1/export")
            .catch(() => setFailed(true))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download aria-hidden="true" />}
        Download everything (JSON)
      </Button>
    </section>
  );
}

/**
 * Credit in and credit out, from our own records rather than Stripe's: it works when Stripe is slow,
 * and it shows credit granted by hand, which Stripe knows nothing about.
 */
function History() {
  const history = useBillingHistory();
  const rows = history.data ?? [];
  if (rows.length === 0) return null;

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-medium">History</h2>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.at}-${index}`} className="border-t border-white/5 first:border-t-0">
                <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                  {new Date(row.at).toLocaleDateString()}
                </td>
                <td className="px-3 py-2">
                  {row.kind}
                  {row.note && <div className="text-[11px] text-muted-foreground">{row.note}</div>}
                </td>
                <td className={cn("px-3 py-2 text-right tabular-nums whitespace-nowrap",
                                  row.amount_usd < 0 && "text-muted-foreground")}>
                  {row.amount_usd < 0 ? "−" : "+"}${Math.abs(row.amount_usd).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right">
                  {row.invoice_url && (
                    <a className="text-xs text-sky-300 underline underline-offset-2"
                       href={row.invoice_url} target="_blank" rel="noreferrer">Invoice</a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#16181d] p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {note && <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>}
    </div>
  );
}
